'use client';

// The single client-side session context (BUG-929), seeded from the SERVER
// (BL-1109).
//
// ── What BUG-929 fixed, and why it still holds ──────────────────────────────
// This was once two providers (`AuthProvider` + `TenantProvider`) hydrating from
// an SSR layout seed (`initialUser` / `initialTenants`). That flipped auth
// `loading` to `false` on the very first render while `currentTenant` was still
// `null` for one effect tick. Every permission gate reads roles as
// `tenantRoles[activeTenantId]`, so during that window `allowed` was spuriously
// `false` and the gate redirected a legitimately-permitted user off the page on a
// hard refresh. BUG-929 merged them into ONE context with ONE `loading` flag and
// removed the seed entirely: `loading` started `true` and only flipped after
// user + tenants + active tenant had all resolved client-side.
//
// ── What BL-1109 changed ────────────────────────────────────────────────────
// Removing the seed cost a whole network leg. The provider fetched
// `/api/auth/me` + `/api/tenants/user` on mount (~250 ms), and only then did
// `currentTenant.id` go non-null — which is what gates every page's data hooks.
// So every dashboard page paid ~250 ms of session round-trip BEFORE it could
// even start its own ~250 ms data round-trip.
//
// The session is now resolved on the server during the HTML render
// (`lib/session/session-bootstrap-service.ts`) and streamed here as a promise.
// `use()` suspends until it lands, so the BUG-929 invariant gets STRONGER, not
// weaker: there is no render in which this provider exposes a half-resolved
// session, because it does not render at all until the session is complete. The
// seed that caused BUG-929 was a *partial* one applied to two providers with two
// loading flags; this is a *complete* one applied to a single context.
//
// The client no longer holds a copy of the active-tenant preference at all. It
// used to live in `localStorage`, which the server can't read; rather than
// mirror it somewhere the server CAN read, BL-1109 deleted the client copy and
// leaned on `staff_users.last_active_tenant_id` — the column the switcher has
// always written on an explicit switch. One server-side source of truth means
// SSR markup and the hydrated tree agree by construction, and it closes BUG-704's
// shared-device hazard structurally: the value is keyed on the user's own row,
// so a device cannot carry one operator's tenant into the next operator's
// session.
//
// `useAuth`, `useTenant`, and `useActiveTenantId` remain at their original import
// paths as thin selectors over this context, so no consumer changed.

import {
    createContext,
    useContext,
    useState,
    useMemo,
    useCallback,
    useRef,
    use,
    type ReactNode,
} from 'react';
import type {
    SessionBootstrapPayload,
    SessionUserDto,
} from '@auctogon/core-admin/schemas/responses/session';
import { Role, Tenant, UserTenant } from '@auctogon/admin-isomorphic/types/user';
import { clearActiveTenantKeys } from '../../api-client/tenants/active-tenant-storage';
import { markSignOutUnderway, startSignOutNavigation } from '../../api-client/sign-out-navigation';
import { api } from '../../api-client/typed-client';
import type { AuthOps } from '../../api-client/auth-ops';
import { clearActiveOfflineScope, useRecordOfflineSession } from '../offline/offline-session';
import { mapMeResponse } from './me-response';
import { saveActiveTenant, TENANT_SWITCH_FAILED, type TenantSwitchOutcome } from './tenant-switch';

export type { TenantSwitchOutcome } from './tenant-switch';

/** Everything the merged session context exposes. `useAuth` / `useTenant` /
 *  `useActiveTenantId` select subsets of this. */
export interface SessionContextType {
    // Auth
    user: SessionUserDto | null;
    /**
     * Resolved auth state. `AuthGuard` reads this to redirect ONLY on a real
     * `'unauthenticated'` — an `'error'` (transient 5xx/network) renders a
     * retryable state instead of bouncing to sign-in (BUG-1182).
     */
    authStatus: AuthStatus;
    tenantRoles: Record<string, Role[]>;
    isSuperAdmin: boolean;
    /** Revoke the server session, then sign out here. See {@link SignOutOutcome}. */
    signOut: () => Promise<SignOutOutcome>;
    refreshUser: () => Promise<void>;
    // Tenant
    currentTenant: UserTenant | null;
    userTenants: UserTenant[];
    allTenants: Tenant[];
    /** Switch the active tenant once the server has saved it (BUG-2469). See {@link TenantSwitchOutcome}. */
    setCurrentTenant: (tenant: UserTenant) => Promise<TenantSwitchOutcome>;
    refreshTenants: () => Promise<void>;
    /**
     * Compat field. Inside this provider it is ALWAYS `false` — `use()` has
     * already suspended until the session resolved, so by the time any consumer
     * renders there is nothing left to wait for. It stays on the context because
     * ~90 call sites read it, and because `useAuth`'s outside-a-provider fallback
     * legitimately returns `true` (no provider ≠ signed out).
     */
    loading: boolean;
    /**
     * The auth operations the app shell injected (BL-1600). On the context
     * because `useProfileModal` needs `changePassword` and cannot take a prop —
     * see `api-client/auth-ops.ts` for why the client itself stays in the app.
     */
    authOps: AuthOps;
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);

/**
 * Outcome of `signOut` (BUG-2071). `SIGN_OUT_UNCONFIRMED`: the server never confirmed the revoke
 * (no connection, or it failed), so nothing was signed out here either and the session stays as
 * it was. The caller says it couldn't sign out.
 */
export type SignOutOutcome = { success: true } | { success: false; code: 'SIGN_OUT_UNCONFIRMED' };


/**
 * Ask Better Auth to revoke the session and clear its cookie.
 *
 * @param authOps - the shell's auth operations.
 * @returns true only when the server answered the sign-out. A thrown fetch (offline) or an error
 *   envelope (the server answered with a failure) means the session may still be live.
 * @sideEffect Revokes the `staff_session` row and clears the session cookie, on success.
 */
async function revokeServerSession(authOps: AuthOps): Promise<boolean> {
    let failure: unknown;
    try {
        const result = await authOps.signOut();
        if (result?.error == null) return true;
        failure = new Error('Sign-out was not confirmed by the server', { cause: result.error });
    } catch (error) {
        failure = error;
    }
    console.error('Logout error:', failure);
    return false;
}

/**
 * The resolved auth state. Distinct from a bare `SessionUserDto | null` so a
 * transient failure (`'error'`) is never confused with a real logout
 * (`'unauthenticated'`) — the BUG-1182 distinction. `'loading'` is the
 * pre-resolution state.
 */
export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated' | 'error';

/**
 * Outcome of a `/api/auth/me` read. A real 401 is `unauthenticated`; a 5xx /
 * network failure is `error` (retryable) — NOT a logout signal. This is the
 * user-side twin of the transient-aware `fetchSessionTenants` below (BUG-1182).
 */
type UserFetchResult =
    | { status: 'authenticated'; user: SessionUserDto }
    | { status: 'unauthenticated' }
    | { status: 'error' };

/**
 * Re-fetch the user from `/api/auth/me`, distinguishing a genuine unauthenticated
 * response from a transient failure. Used by the REFRESH paths only — the initial
 * session comes from the server (BL-1109).
 *
 * - `res.ok` with a user → `authenticated`; `res.ok` with no user → `unauthenticated`.
 * - HTTP **401** → `unauthenticated` (a real, confirmed logout).
 * - Any other non-ok (5xx) or a thrown/network error → `error` (retryable). The
 *   caller MUST NOT treat this as a logout — that was the BUG-1182 defect, where
 *   a blip on the `staff_users` auth read bounced a still-valid operator to
 *   sign-in.
 */
async function fetchSessionUser(): Promise<UserFetchResult> {
    try {
        const res = await api.auth.me.get.raw({
            init: { cache: 'no-store', headers: { 'Cache-Control': 'no-store' } },
        });
        if (res.ok) {
            const user = mapMeResponse(await res.json());
            return user ? { status: 'authenticated', user } : { status: 'unauthenticated' };
        }
        if (res.status === 401) return { status: 'unauthenticated' };
        return { status: 'error' };
    } catch (error) {
        console.error('[SessionProvider] failed to fetch user', error);
        return { status: 'error' };
    }
}

/** Resolved tenant lists from `/api/tenants/user`. */
interface TenantLists {
    userTenants: UserTenant[];
    allTenants: Tenant[];
}

/**
 * Re-fetch the user's tenant lists from `/api/tenants/user` (the REFRESH path).
 * @returns the resolved tenant lists, or `null` when the fetch FAILED (non-ok /
 *   network error). `null` is distinct from a successful empty result
 *   (`{ userTenants: [], allTenants: [] }`) so callers can preserve existing
 *   state on failure instead of wiping it — a transient refresh failure must not
 *   drop a permitted user's active tenant and bounce them off the page.
 */
async function fetchSessionTenants(): Promise<TenantLists | null> {
    try {
        const body = await api.tenants.user.get({ init: { cache: 'no-store' } });
        if (!body.success) {
            console.error('[SessionProvider] tenants API error', new Error(body.error));
            return null;
        }
        return { userTenants: body.data.userTenants, allTenants: body.data.allTenants };
    } catch (error) {
        console.error('[SessionProvider] failed to fetch tenants', error);
        return null;
    }
}

/**
 * Pick the active tenant from a re-fetched list, for the REFRESH paths only.
 *
 * The server already stamped `isDefault` from `staff_users.last_active_tenant_id`,
 * so this just honours that stamp (falling back to the alphabetical-first entry
 * when the list carries none). Identical precedence to the initial server-side
 * resolution, because it reads the same server-computed field.
 */
function resolveTenantAfterRefresh(tenants: UserTenant[]): UserTenant | null {
    if (tenants.length === 0) return null;
    return tenants.find((t) => t.isDefault) ?? tenants[0];
}

/**
 * The single client-side session provider.
 *
 * @param sessionPromise - the server-resolved session, streamed from the layout.
 *   Consumed with `use()`, so this component suspends until it settles and every
 *   consumer's first render already sees a complete session.
 * @sideEffect None on mount — the session arrives fully resolved from the server.
 */
export function SessionProvider({
    sessionPromise,
    authOps,
    children,
}: {
    sessionPromise: Promise<SessionBootstrapPayload>;
    /**
     * Auth operations supplied by the app shell (BL-1600). The Better Auth
     * client cannot live in this package — see `api-client/auth-ops.ts`.
     */
    authOps: AuthOps;
    children: ReactNode;
}) {
    // Pin the FIRST promise. The `(dashboard)` layout is dynamic (it reads
    // `headers()` for `x-pathname` to choose the shell) and any `router.refresh()`
    // re-renders it, so a later render can hand down a NEW promise. Unpinned,
    // `use()` would suspend again and flash the whole shell back to the Suspense
    // fallback. Only the first value is authoritative — after that the client owns
    // live session state, with `refreshUser` / `refreshTenants` as the update paths.
    // `useState`'s initializer (not a ref) because this IS read during render.
    const [firstSessionPromise] = useState(sessionPromise);
    const initial = use(firstSessionPromise);

    const initialAuthenticated = initial.status === 'authenticated' ? initial : null;
    const initialTenants = initialAuthenticated?.userTenants ?? [];

    const [user, setUser] = useState<SessionUserDto | null>(initialAuthenticated?.user ?? null);
    const [authStatus, setAuthStatus] = useState<AuthStatus>(initial.status);
    const [userTenants, setUserTenants] = useState<UserTenant[]>(initialTenants);
    const [allTenants, setAllTenants] = useState<Tenant[]>(initialAuthenticated?.allTenants ?? []);
    // Derived from the SERVER-resolved id, never re-derived on the client — that
    // is what keeps SSR markup and the hydrated tree identical.
    const [currentTenant, setCurrentTenantState] = useState<UserTenant | null>(
        () => initialTenants.find((t) => t.id === initialAuthenticated?.activeTenantId) ?? null,
    );

    // Read currentTenant inside refresh callbacks without widening their deps.
    const currentTenantRef = useRef(currentTenant);
    currentTenantRef.current = currentTenant;
    // Tracks the last resolved user id so `refreshUser` can detect a genuine
    // identity change and drop the previous identity's tenant selection.
    const userIdRef = useRef<string | null>(initialAuthenticated?.user.id ?? null);
    // Tenant switches run one after another (BUG-2469), so the server saves them in the order they
    // land here and the two never end on different houses.
    const switchQueueRef = useRef<Promise<unknown>>(Promise.resolve());

    /**
     * Switch the active tenant. An explicit user switch is the ONLY thing that
     * persists a preference (BUG-704), and the server is the only place it is kept,
     * since the client keeps no copy. The switch lands only once the server has
     * saved it (BUG-2469): until `PUT /api/user/active-tenant` succeeds the current
     * tenant stays, so the app never shows a house a reload would not reopen.
     * Offline the request cannot be sent, so the switch fails and nothing changes.
     * @returns {@link TenantSwitchOutcome}; never rejects.
     * @sideEffect Fires `PUT /api/user/active-tenant`.
     */
    const switchNow = useCallback(async (tenant: UserTenant, userId: string | null): Promise<TenantSwitchOutcome> => {
        // The user who asked has signed out (or another signed in) while it waited its turn.
        if (userIdRef.current !== userId) return { success: false, error: TENANT_SWITCH_FAILED };
        if (currentTenantRef.current?.id === tenant.id) return { success: true };
        const saved = await saveActiveTenant(tenant.id);
        if (!saved.success) return saved;
        // Signed out, or someone else signed in, while the request was in flight.
        if (userIdRef.current !== userId) return { success: false, error: TENANT_SWITCH_FAILED };
        setCurrentTenantState(tenant);
        // The next queued switch reads this before React re-renders.
        currentTenantRef.current = tenant;
        return { success: true };
    }, []);

    /**
     * Switch the active tenant. An explicit user switch is the ONLY thing that
     * persists a preference (BUG-704), and the server is the only place it is kept,
     * since the client keeps no copy. The switch lands only once the server has
     * saved it (BUG-2469): until `PUT /api/user/active-tenant` succeeds the current
     * tenant stays, so the app never shows a house a reload would not reopen.
     * Switches queue behind one another, so the house shown is always the server's
     * last saved one. Offline the request cannot be sent, so the switch fails and
     * nothing changes.
     * @returns {@link TenantSwitchOutcome}; never rejects.
     * @sideEffect Fires `PUT /api/user/active-tenant`.
     */
    const setCurrentTenant = useCallback((tenant: UserTenant): Promise<TenantSwitchOutcome> => {
        // The asking user is captured now, not when the switch's turn comes.
        const userId = userIdRef.current;
        const run = switchQueueRef.current.then(() => switchNow(tenant, userId));
        switchQueueRef.current = run;
        return run;
    }, [switchNow]);

    /**
     * Re-fetch the tenant lists (e.g. after a settings save). Does NOT flip the
     * app-level `loading` — that flag is initial-session-resolution only; blanking
     * the whole shell for a tenant refresh would be wrong. On a FAILED re-fetch it
     * preserves the existing lists + active tenant (a transient hiccup must not
     * wipe a permitted user's tenant and bounce them off the page). Re-resolves the
     * active tenant only when the current one is gone from the new list.
     */
    const refreshTenants = useCallback(async () => {
        const tenants = await fetchSessionTenants();
        if (!tenants) return; // failure — keep existing state, don't wipe to empty
        setUserTenants(tenants.userTenants);
        setAllTenants(tenants.allTenants);
        const current = currentTenantRef.current;
        if (!current || !tenants.userTenants.some((t) => t.id === current.id)) {
            setCurrentTenantState(resolveTenantAfterRefresh(tenants.userTenants));
        }
    }, []);

    /**
     * Re-fetch the user (e.g. after a profile edit / role change). If the resolved
     * identity CHANGED (a different user id), the previous identity's tenant
     * selection is stale: clear the saved preference and re-resolve tenants for the
     * new identity so a stale/foreign selection can't survive an identity change
     * (the invariant the former TenantProvider's A→B guard held — BUG-643 lineage).
     */
    const refreshUser = useCallback(async () => {
        const result = await fetchSessionUser();
        // Transient failure — keep prior state (don't wipe a valid session, don't
        // flip a working session into an error screen). Retryable; the next call
        // (e.g. AuthGuard's Retry) resolves it. Mirrors refreshTenants (BUG-1182).
        if (result.status === 'error') return;
        const nextUser = result.status === 'authenticated' ? result.user : null;
        setUser(nextUser);
        setAuthStatus(result.status);
        const nextId = nextUser?.id ?? null;
        if (nextId !== userIdRef.current) {
            userIdRef.current = nextId;
            clearActiveTenantKeys();
            const tenants = await fetchSessionTenants();
            if (!tenants) return; // failure — leave prior lists; next refresh retries
            setUserTenants(tenants.userTenants);
            setAllTenants(tenants.allTenants);
            setCurrentTenantState(resolveTenantAfterRefresh(tenants.userTenants));
        }
    }, []);

    /**
     * Sign out: revoke the Better Auth session (BL-787B — was the WorkOS
     * `POST /api/auth/logout` route, which post-cutover cleared the unused WorkOS
     * cookie but NOT the Better Auth `auctogon-admin.session_token`, so the user
     * stayed logged in). `authClient.signOut()` clears the session cookie and
     * revokes the `staff_session` row. Then sweep the retired active-tenant
     * localStorage keys off the device (hygiene — nothing reads them post-BL-1109)
     * and redirect.
     *
     * BUG-2071: the revoke must land FIRST. With no connection it never reaches the
     * server, and the httpOnly cookie a client cannot clear would hand the session
     * back to whoever opens the app once it reconnects. So an unconfirmed revoke
     * signs nothing out here either: no state is cleared, no redirect, the device's
     * offline pointer stays, and the caller says it couldn't sign out.
     * @returns `{ success: true }` once signed out, else `SIGN_OUT_UNCONFIRMED`.
     * @sideEffect Calls Better Auth sign-out; on success clears the offline pointer and the retired `localStorage` keys and navigates.
     */
    const signOut = useCallback(async (): Promise<SignOutOutcome> => {
        if (!(await revokeServerSession(authOps))) return { success: false, code: 'SIGN_OUT_UNCONFIRMED' };
        // The cookie is gone from here on, so a call still in flight 401s. Say so now, before the
        // awaited cleanup below, so the 401 handler does not start a navigation of its own (BUG-2233).
        markSignOutUnderway();
        // The server session is revoked, so the page always goes to sign-in, even when a cleanup
        // step throws: the error still reaches the caller, but the flag set above would otherwise
        // silence every other redirect and strand the operator on a dead session (BUG-2233).
        try {
            // BL-2343: an offline cold start must not reopen this session. The device store itself
            // is held (ADR 066 — Offline Mode § 6.1 limit 3); only its active-scope pointer goes.
            await clearActiveOfflineScope();
            clearActiveTenantKeys();
            // Reset every identity-scoped field so a shared device can't render the
            // prior user's tenant context between sign-out and the page load (or after Back).
            userIdRef.current = null;
            setUser(null);
            setAuthStatus('unauthenticated');
            setUserTenants([]);
            setAllTenants([]);
            setCurrentTenantState(null);
        } finally {
            // BUG-2233: the ONE navigation. A soft `router.replace` / `router.refresh` beside it
            // cancels it, and in the phone app a cancelled load shows "Can't reach Auctogon".
            startSignOutNavigation();
        }
        return { success: true };
    }, [authOps]);

    // BL-2343: in the phone app, keep the copy an offline cold start opens (ADR 066 § 2).
    useRecordOfflineSession({ authStatus, user, currentTenant, userTenants, allTenants });

    // Derived fields (tenantRoles/isSuperAdmin) are read straight off `user` here
    // rather than via separate memos: `user` is already a dep, so the value
    // recomputes on exactly the same schedule, with less indirection.
    const value = useMemo<SessionContextType>(
        () => ({
            user,
            authStatus,
            tenantRoles: user?.tenantRoles ?? {},
            isSuperAdmin: !!user?.isSuperAdmin,
            signOut,
            refreshUser,
            currentTenant,
            userTenants,
            allTenants,
            setCurrentTenant,
            refreshTenants,
            // Always false inside the provider — `use()` already waited. See the
            // field's doc comment on SessionContextType.
            loading: false,
            authOps,
        }),
        [
            user,
            authStatus,
            signOut,
            refreshUser,
            currentTenant,
            userTenants,
            allTenants,
            setCurrentTenant,
            refreshTenants,
            authOps,
        ],
    );

    return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

/**
 * Raw session context accessor. Returns `undefined` outside a provider — callers
 * that must tolerate that (static generation, isolated tests) handle the fallback
 * themselves (see `useAuth`). Most consumers use the `useAuth` / `useTenant`
 * selectors instead of this.
 */
export function useSessionContext(): SessionContextType | undefined {
    return useContext(SessionContext);
}

/**
 * The shell-injected auth operations (BL-1600).
 *
 * Fails at the point of USE, not the point of CALL. Outside a provider this
 * returns operations that throw when invoked, rather than throwing on render.
 *
 * Both halves are deliberate. There is no honest no-op for `changePassword` — a
 * silent stub would report success without changing a credential — so a missing
 * provider must be loud. But rule #7 forbids conditional hooks, so a component
 * that merely SITS NEXT TO the password flow still has to call this; throwing on
 * render would take out unrelated UI. (Measured: it broke all six
 * NotificationsTab suites, which never touch a password.)
 *
 * `useAuth`'s permissive fallback is a different case and stays as it is — "no
 * provider" is not "signed out".
 */
export function useAuthOps(): AuthOps {
    const ctx = useContext(SessionContext);
    return useMemo<AuthOps>(() => {
        if (ctx) return ctx.authOps;
        const missing = () => {
            throw new Error(
                'useAuthOps was invoked outside a SessionProvider — the app shell supplies authOps (BL-1600).',
            );
        };
        return { signOut: missing, changePassword: missing };
    }, [ctx]);
}
